/** @typedef {import('headlight-configurable-application').Pict} Pict */

const SelectInputTypeProvider = require('./PictProvider-InputType-Select.js');

const libVocabularySelectorMap = require('./_VocabularySelectorMap.js');

const DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS = libVocabularySelectorMap.DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS;

/**
 * Sample-status selector. The vocabulary lives in the customer-managed `SampleStatus` table and is
 * resolved through the Select base class's entity-backed option source (Hash -> the code stored in
 * `Sample.Status`, Name -> the label, ordered by Ordinal). A customer with no SampleStatus rows falls
 * back to DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS, so the input works before any list is curated.
 */
class SampleStatusSelectorInputType extends SelectInputTypeProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Point the input at the managed SampleStatus vocabulary and install the fallback list. A schema
	 * that carries its own SelectOptions / SelectOptionsEntity keeps them.
	 *
	 * @param {Object} pInput - The input object.
	 */
	_applySampleStatusOptionSource(pInput)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput, 'SampleStatusSelector');
	}

	/**
	 * The option list this input is actually rendering - the managed vocabulary once it has loaded,
	 * otherwise the fallback. Used to label the non-interactive Read / PDF nodes.
	 *
	 * @param {Object} pInput - The input object.
	 * @param {Function} [fOnLoaded] - Invoked once if the vocabulary is still loading, so the caller can repaint.
	 * @return {Array<{id: any, text: string}>} - The resolved option list.
	 */
	_getResolvedStatusOptions(pInput, fOnLoaded)
	{
		const tmpEntityOptions = this.resolveEntitySelectOptions(pInput, fOnLoaded);
		if (Array.isArray(tmpEntityOptions) && tmpEntityOptions.length > 0)
		{
			return tmpEntityOptions;
		}
		return (Array.isArray(pInput?.PictForm?.SelectOptions) && pInput.PictForm.SelectOptions.length > 0) ?
			pInput.PictForm.SelectOptions :
			DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#SAMPLE-STATUS-SELECT-FOR-${pInputHTMLID}`;
	}

	getDisplayInputHTMLID(pInputHTMLID)
	{
		return `#SAMPLE-STATUS-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#SAMPLE-STATUS-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#SAMPLE-STATUS-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularDisplayInputID(pInputHTMLID, pRowIndex)
	{
		return `#SAMPLE-STATUS-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTrackingGUIDID(pInputHTMLID, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${pInputHTMLID}`;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applySampleStatusOptionSource(pInput);
		//TODO: are there cases where we want to auto-set this based on test results? Probably based on a config flag, and only if there isn't an existing value set.
		//      Maybe put a refresh button??? Or hash things and check for the hash changing? Think about it
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applySampleStatusOptionSource(pInput);
		//TODO: are there cases where we want to auto-set this based on test results? Probably based on a config flag, and only if there isn't an existing value set.
		//      Maybe put a refresh button??? Or hash things and check for the hash changing? Think about it
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex
	 * @param {string} pEvent
	 * @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		//TODO: since the test reult event is bound to the test, we can't validate its tracking GUID against the sample's tracking GUID here
		//      do we want to daisy-chain this event through thest to pick up the sample tracking GUID?
		if (tmpEventType === 'TestResultChanged')
		{
			if (pInput.PictForm?.AutoUpdate === false || pInput.PictForm?.AutoUpdate === 'false')
			{
				return;
			}
			this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(pTransactionGUID, () =>
			{
				if (pInput.PictForm.Disabled !== true)
				{
					this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), '__TestResultAggregate', []);
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestStatusAggregate:${this._getTrackingGUID(pInput, pRowIndex)}:__TestResultAggregate`);
				}
			});
		}

		if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}

		if (tmpEventType === 'TestStatusAggregate')
		{
			if (pInput.PictForm?.AutoUpdate === false || pInput.PictForm?.AutoUpdate === 'false')
			{
				return;
			}
			const tmpStatusGroupAddress = tmpEventPayload;
			const tmpStatusGroup = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpStatusGroupAddress);
			if (!Array.isArray(tmpStatusGroup))
			{
				return;
			}
			let tmpAddress;
			if (pRowIndex != null)
			{
				tmpAddress = pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
			}
			else
			{
				tmpAddress = pInput.DataAddress;
			}
			const tmpCurrentStatus = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress);
			const tmpNewStatus = this._computeNewSampleStatusFromTestResults(tmpStatusGroup, tmpCurrentStatus);
			if (tmpNewStatus)
			{
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress, tmpNewStatus);
				pView.manualMarshalDataToViewByInput(pInput, pTransactionGUID);
				pView.dataChanged(pInput.Hash);
			}
			return;
		}
		if (tmpEventType === 'SampleChanged')
		{
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (tmpSampleRecord && typeof tmpSampleRecord === 'object' && !Array.isArray(tmpSampleRecord) && tmpSampleRecord.Status)
			{
				let tmpAddress;
				if (pRowIndex != null)
				{
					tmpAddress = pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
				}
				else
				{
					tmpAddress = pInput.DataAddress;
				}
				const tmpCurrentStatus = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress);
				if (tmpCurrentStatus !== tmpSampleRecord.Status)
				{
					this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress, tmpSampleRecord.Status);
					pView.manualMarshalDataToViewByInput(pInput, pTransactionGUID);
				}
			}
			return;
		}
	}

	/**
	 * @param {Array<string>} pTestResultsArray
	 * @param {string} pSampleStatus
	 *
	 * @return {string | null}
	 */
	_computeNewSampleStatusFromTestResults(pTestResultsArray, pSampleStatus)
	{
		let tmpNewSampleStatus;
		let tmpSeenPendingTest = false;
		let tmpSeenFinalizedTest = false;
		let tmpSeenFailingTest = false;
		let tmpSeenPassingTest = false;
		for (const tmpTestResult of pTestResultsArray)
		{
			if (!tmpTestResult)
			{
				tmpSeenPendingTest = true;
				break;
			}
			if (tmpTestResult.startsWith('FAI'))
			{
				tmpSeenFailingTest = true;
				tmpSeenFinalizedTest = true;
			}
			if (tmpTestResult.startsWith('PAS'))
			{
				tmpSeenPassingTest = true;
				tmpSeenFinalizedTest = true;
			}
		}
		// we won't move the sample to a "higher" status automatically
		if (tmpSeenFailingTest)
		{
			tmpNewSampleStatus = 'FAIL';
			if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC')
			{
				this.pict.log.info(`SampleStatusSelector: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		else if (tmpSeenPendingTest)
		{
			// if any test is pending, sample is pending
			tmpNewSampleStatus = tmpSeenFinalizedTest ? 'TEST' : 'PEND';
			if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC' && pSampleStatus != tmpNewSampleStatus)
			{
				this.pict.log.info(`SampleStatusSelector: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		else if (tmpSeenPassingTest)
		{
			tmpNewSampleStatus = 'PASS';
			if (!pSampleStatus || pSampleStatus == 'N/A' || pSampleStatus == 'LOGD' || pSampleStatus == 'RECV' || pSampleStatus == 'INFO' || pSampleStatus == 'PEND' || pSampleStatus == 'TEST' || pSampleStatus == 'TSTR')
			{
				this.pict.log.info(`SampleStatusSelector: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		return null;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applySampleStatusOptionSource(pInput);
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput);
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// for PDF and locked/read-only views, we need to set the display text as well
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			const tmpPrintSelector = this.getDisplayInputHTMLID(pInput.Macro.RawHTMLID);
			const fRepaint = () => this.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
			const tmpOption = this._getResolvedStatusOptions(pInput, fRepaint).find((pOption) => pOption.id === pValue);
			const tmpDisplayText = tmpOption ? tmpOption.text : pValue;
			this.pict.ContentAssignment.assignContent(tmpPrintSelector, tmpDisplayText);
		}
		return tmpResult;
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applySampleStatusOptionSource(pInput);
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		//TODO: plumb the test result into the configured address
		// for PDF and locked/read-only views, we need to set the display text as well
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			const tmpPrintSelector = this.getTabularDisplayInputID(pInput.Macro.RawHTMLID, pRowIndex);
			const fRepaint = () => this.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
			const tmpOption = this._getResolvedStatusOptions(pInput, fRepaint).find((pOption) => pOption.id === pValue);
			const tmpDisplayText = tmpOption ? tmpOption.text : pValue;
			this.pict.ContentAssignment.assignContent(tmpPrintSelector, tmpDisplayText);
		}
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the change event for the data in the select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input.
	 * @param {string} pHTMLSelector - The HTML selector of the input.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the super.onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
		if (pInput.PictForm?.EmitStatusChange !== false)
		{
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`SampleStatusChanged:${this._getTrackingGUID(pInput)}:${pValue}`);
		}
	}

	/**
	 * Handles the change event for tabular data.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the super method.
	 */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm?.EmitStatusChange !== false)
		{
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`SampleStatusChanged:${this._getTrackingGUID(pInput, pRowIndex)}:${pValue}`);
		}
	}

	_getTrackingGUID(pInput, pRowIndex)
	{
		if (pRowIndex == null && pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			return pInput.PictForm.SampleTrackingGUID;
		}
		return this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex));
	}

	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID)
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex), pTrackingGUID);
	}

	/**
	 * Searches for the sample descriptor that this test result should be bound to and inherits its tracking GUID if needed.
	 *
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		// modalities:
		//
		// single global sample
		// tabular row-based tests
		// tabular row-based sample and tests

		if (pRowIndex != null)
		{
			// tabular
			const tmpLocalManifest = pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.log.error('SampleStatusSelector: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}
		// check sibling Descriptors for a sample descriptor
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.log.error('SampleStatusSelector: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, null);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.log.warn('SampleStatusSelector: could not find sibling sample descriptor to bind tracking GUID from.');
		}
	}
}

module.exports = SampleStatusSelectorInputType;

module.exports.DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS = DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS;
